import { useState, useEffect } from 'react';
import { Button } from '../ui';
import PremiumName from '../ui/PremiumName';
import { useUserData } from '../../hooks/useUserData';
import { createServiceOrder, uploadReferenceImage } from '../../services/orderService';
import { v4 as uuidv4 } from 'uuid';

const DEBUG = false;
const debugLog = (...args) => {
  if (DEBUG) console.log(...args);
};

/**
 * RequestServiceModal - Modal para solicitar un servicio
 * Permite al cliente especificar detalles y paquete deseado
 */
const RequestServiceModal = ({ service, onClose, onSuccess }) => {
  const { userData: sellerData } = useUserData(service?.userId);
  const [selectedPackageIndex, setSelectedPackageIndex] = useState(0);
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referenceImages, setReferenceImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  // Seleccionar primer paquete por defecto (índice 0)
  useEffect(() => {
    debugLog('🎯 Service recibido:', service);
    debugLog('💰 Prices:', service.prices);
    debugLog('📊 Length:', service.prices?.length);
    
    if (service.prices && service.prices.length > 0) {
      setSelectedPackageIndex(0);
      debugLog('✅ Índice inicial establecido en 0');
    } else {
      console.error('❌ No hay paquetes disponibles!');
    }
  }, [service]);

  // Obtener paquete seleccionado actual
  const selectedPackage = service.prices && service.prices.length > 0 && service.prices[selectedPackageIndex] 
    ? service.prices[selectedPackageIndex] 
    : null;

  // Nota: logs aquí se ejecutan en cada render (incluyendo al tipear). Mantenerlos detrás de DEBUG.
  debugLog('🎁 Paquete seleccionado calculado:', selectedPackage);
  debugLog('🔢 Índice actual:', selectedPackageIndex);

  // Si el servicio no tiene paquetes, mostrar error
  if (!service.prices || service.prices.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md p-6">
          <div className="text-center">
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Error: Servicio sin paquetes
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Este servicio no tiene paquetes de precios configurados. 
              Contacta al vendedor para más información.
            </p>
            <Button variant="gradient" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Handler para seleccionar imágenes
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validar cantidad
    if (referenceImages.length + files.length > 5) {
      alert('Puedes subir máximo 5 imágenes de referencia');
      return;
    }
    
    // Validar tipo y tamaño
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} no es una imagen válida`);
        return false;
      }
      // Validar tamaño (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} es muy grande. Máximo 5MB por imagen`);
        return false;
      }
      return true;
    });
    
    // Crear previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setReferenceImages(prev => [...prev, {
          file,
          preview: reader.result,
          id: uuidv4()
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (imageId) => {
    setReferenceImages(prev => prev.filter(img => img.id !== imageId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    debugLog('📋 Iniciando envío de solicitud...');
    debugLog('📦 Paquete seleccionado:', selectedPackage);
    debugLog('📦 Índice seleccionado:', selectedPackageIndex);
    debugLog('📝 Descripción:', description);
    debugLog('🖼️ Imágenes de referencia:', referenceImages.length);

    // Validación robusta del paquete
    if (!service.prices || service.prices.length === 0) {
      alert('Error: Este servicio no tiene paquetes disponibles');
      console.error('❌ service.prices no existe o está vacío');
      return;
    }

    if (selectedPackageIndex === null || selectedPackageIndex === undefined) {
      alert('Error: No se ha seleccionado ningún índice de paquete');
      console.error('❌ selectedPackageIndex:', selectedPackageIndex);
      return;
    }

    const pkg = service.prices[selectedPackageIndex];
    
    if (!pkg) {
      alert('Error: El paquete seleccionado no existe');
      console.error('❌ Paquete en índice', selectedPackageIndex, 'no encontrado');
      console.error('Paquetes disponibles:', service.prices);
      return;
    }

    // Los paquetes pueden tener "name" o "details" (CreateServiceModal usa "details")
    const packageName = pkg.name || pkg.details;
    if (!packageName || pkg.price == null || pkg.price === '') {
      alert('Error: El paquete no tiene nombre o precio');
      console.error('❌ Paquete incompleto:', pkg);
      return;
    }

    if (!description.trim()) {
      alert('Por favor describe lo que necesitas');
      return;
    }

    console.log('✅ Todas las validaciones pasadas, procediendo...');

    setIsSubmitting(true);
    
    try {
      // Subir imágenes de referencia primero
      let referenceUrls = [];
      
      if (referenceImages.length > 0) {
        setUploadingImages(true);
        const tempOrderId = uuidv4(); // ID temporal para organizar archivos
        
        const uploadPromises = referenceImages.map(img => 
          uploadReferenceImage(img.file, tempOrderId)
        );
        
        referenceUrls = await Promise.all(uploadPromises);
        setUploadingImages(false);
      }

      const orderData = {
        serviceId: service.id,
        serviceName: service.caption || 'Servicio',
        serviceImage: service.media && service.media[0] ? 
          (typeof service.media[0] === 'string' ? service.media[0] : service.media[0].url) : null,
        sellerId: service.userId,
        packageType: packageName,  // name o details
        packageName: packageName,
        amount: Number(pkg.price),
        currency: service.currency || 'ARS',
        description: description.trim(),
        deadline: deadline || null,
        referenceFiles: referenceUrls // Ahora con URLs reales
      };

      console.log('📤 Enviando orderData:', orderData);

      const result = await createServiceOrder(orderData);

      if (result.success) {
        if (onSuccess) {
          onSuccess(result.order);
        }
        onClose();
      } else {
        alert('Error al crear la solicitud: ' + result.error);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al procesar la solicitud: ' + error.message);
    } finally {
      setIsSubmitting(false);
      setUploadingImages(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div 
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Solicitar Servicio
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            disabled={isSubmitting}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Info del servicio */}
          <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4">
            <div className="flex items-center gap-4">
              {service.media && service.media[0] && (
                <img
                  src={typeof service.media[0] === 'string' ? service.media[0] : service.media[0].url}
                  alt={service.caption}
                  className="w-20 h-20 object-cover rounded-lg"
                />
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                  {service.caption}
                </h3>
                <PremiumName
                  name={service.userName || sellerData?.displayName || 'Vendedor'}
                  isPremium={sellerData?.isPremium}
                  premiumColor={sellerData?.premiumNameColor}
                  nameClassName="text-sm text-gray-600 dark:text-gray-400 block"
                />
              </div>
            </div>
          </div>

          {/* Selección de paquete */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
              Selecciona un paquete *
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {service.prices && service.prices.map((pkg, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setSelectedPackageIndex(index)}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    selectedPackageIndex === index
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-slate-600 hover:border-indigo-400'
                  }`}
                >
                  <h4
                    className="font-semibold text-gray-900 dark:text-white mb-1 leading-snug line-clamp-2 break-words"
                    title={pkg.name || pkg.details || 'Paquete'}
                  >
                    {pkg.name || pkg.details || 'Paquete'}
                  </h4>
                  <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    ${pkg.price}
                  </p>
                  {pkg.deliveryTime && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {pkg.deliveryTime}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Descripción del pedido */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
              ¿Qué necesitas? *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe en detalle lo que necesitas para este servicio..."
              rows={6}
              required
              className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Sé específico: estilo, colores, tamaño, formato, etc.
            </p>
          </div>

          {/* Fecha límite (opcional) */}
          {service.allowClientDescription && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Fecha límite (opcional)
              </label>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Imágenes de referencia */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
              Imágenes de referencia (opcional)
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Sube hasta 5 imágenes como referencia (máximo 5MB cada una)
            </p>
            
            {/* Previews de imágenes */}
            {referenceImages.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-3">
                {referenceImages.map((img) => (
                  <div key={img.id} className="relative group">
                    <img
                      src={img.preview}
                      alt="Referencia"
                      className="w-full aspect-square object-cover rounded-lg border-2 border-gray-200 dark:border-slate-600"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(img.id)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Botón para agregar imágenes */}
            {referenceImages.length < 5 && (
              <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl hover:border-indigo-500 dark:hover:border-indigo-400 transition-colors cursor-pointer bg-gray-50 dark:bg-slate-700/50">
                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Agregar imagen ({referenceImages.length}/5)
                </span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                  disabled={isSubmitting}
                />
              </label>
            )}
          </div>

          {/* Nota informativa */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <div className="flex gap-3">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-900 dark:text-blue-100">
                <p className="font-semibold mb-1">¿Cómo funciona?</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-800 dark:text-blue-200">
                  <li>El vendedor revisará tu solicitud</li>
                  <li>Si acepta, recibirás un email con el link de pago</li>
                  <li>Una vez pagado, el vendedor comenzará a trabajar</li>
                  <li>Recibirás los entregables cuando esté completado</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="gradient"
              loading={isSubmitting}
              className="flex-1"
            >
              {uploadingImages ? 'Subiendo imágenes...' : isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestServiceModal;
